import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ── Shared types ─────────────────────────────────────────────────────────────

export interface BillingConfig {
  pricePerApartmentMonthly: number;
  pricePerApartmentAnnual: number;
  currency: string;
  suspendBlockReservations: boolean;
  suspendBlockApartments: boolean;
  suspendBlockCalendarSync: boolean;
  suspendBlockExport: boolean;
  suspendBlockUbyport: boolean;
}

export interface BillingRecord {
  id: number;
  managerId: number;
  managerName: string;
  managerEmail: string;
  periodYear: number;
  periodMonth: number;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  apartmentCount: number;
  pricePerApartment: number;
  totalAmount: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface CreateBillingRecordRequest {
  managerId: number;
  periodYear: number;
  periodMonth: number;
  apartmentCount: number;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  status?: 'PENDING' | 'PAID' | 'OVERDUE';
  notes?: string;
}

export interface UpdateBillingRecordRequest {
  status?: 'PENDING' | 'PAID' | 'OVERDUE';
  paidAt?: string;
  apartmentCount?: number;
  notes?: string;
}

export interface BillingSummary {
  totalDue: number;
  totalPaid: number;
  totalOverdue: number;
  recordCount: number;
}

export interface ApartmentLimitRequest {
  id: number;
  managerId: number;
  managerName: string;
  managerEmail: string;
  currentCount: number;
  currentLimit: number;
  message: string | null;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  newLimit: number | null;
  adminNotes: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface ApartmentBillingInfo {
  id: number;
  name: string;
  address: string | null;
  isActive: boolean;
  billingStartDate: string | null;
  isBillableThisMonth: boolean;
  monthlyPrice: number;
}

export interface ManagerBillingSubscription {
  billingStatus: string;
  activeApartmentCount: number;
  monthlyCost: number;
  monthlyPricePerApartment: number;
  annualPricePerApartment: number;
  currency: string;
  apartments: ApartmentBillingInfo[];
  records: BillingRecord[];
}

// ── Admin API ─────────────────────────────────────────────────────────────────

export const adminGetBillingConfig = async (token: string): Promise<BillingConfig> => {
  const res = await axios.get(`${API_BASE}/admin/billing/config`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminUpdateBillingConfig = async (
  payload: BillingConfig,
  token: string
): Promise<BillingConfig> => {
  const res = await axios.put(`${API_BASE}/admin/billing/config`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminGetAllBillingRecords = async (token: string): Promise<BillingRecord[]> => {
  const res = await axios.get(`${API_BASE}/admin/billing/records`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminGetBillingRecordsByManager = async (
  managerId: number,
  token: string
): Promise<BillingRecord[]> => {
  const res = await axios.get(`${API_BASE}/admin/billing/records/${managerId}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminCreateBillingRecord = async (
  payload: CreateBillingRecordRequest,
  token: string
): Promise<BillingRecord> => {
  const res = await axios.post(`${API_BASE}/admin/billing/records`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminUpdateBillingRecord = async (
  id: number,
  payload: UpdateBillingRecordRequest,
  token: string
): Promise<BillingRecord> => {
  const res = await axios.put(`${API_BASE}/admin/billing/records/${id}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminDeleteBillingRecord = async (id: number, token: string): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/billing/records/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const adminGetBillingSummary = async (token: string): Promise<BillingSummary> => {
  const res = await axios.get(`${API_BASE}/admin/billing/summary`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminSuspendManager = async (managerId: number, token: string): Promise<void> => {
  await axios.post(`${API_BASE}/admin/billing/managers/${managerId}/suspend`, null, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const adminActivateManager = async (managerId: number, token: string): Promise<void> => {
  await axios.post(`${API_BASE}/admin/billing/managers/${managerId}/activate`, null, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const adminGetManagerApartmentsBilling = async (
  managerId: number,
  token: string
): Promise<ApartmentBillingInfo[]> => {
  const res = await axios.get(`${API_BASE}/admin/billing/managers/${managerId}/apartments`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminGenerateBills = async (token: string): Promise<{ created: number; message: string }> => {
  const res = await axios.post(`${API_BASE}/admin/billing/generate`, null, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminGetLimitRequests = async (token: string): Promise<ApartmentLimitRequest[]> => {
  const res = await axios.get(`${API_BASE}/admin/billing/limit-requests`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminApproveLimitRequest = async (
  id: number,
  newLimit: number,
  adminNotes: string,
  token: string
): Promise<ApartmentLimitRequest> => {
  const res = await axios.post(`${API_BASE}/admin/billing/limit-requests/${id}/approve`, { newLimit, adminNotes }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const adminDenyLimitRequest = async (
  id: number,
  adminNotes: string,
  token: string
): Promise<ApartmentLimitRequest> => {
  const res = await axios.post(`${API_BASE}/admin/billing/limit-requests/${id}/deny`, { adminNotes }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// ── Manager API ───────────────────────────────────────────────────────────────

export const getMySubscription = async (token: string): Promise<ManagerBillingSubscription> => {
  const res = await axios.get(`${API_BASE}/manager/billing/subscription`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getMyBillingRecords = async (token: string): Promise<BillingRecord[]> => {
  const res = await axios.get(`${API_BASE}/manager/billing/records`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const submitApartmentLimitRequest = async (
  currentCount: number,
  currentLimit: number,
  message: string,
  token: string
): Promise<ApartmentLimitRequest> => {
  const res = await axios.post(`${API_BASE}/manager/billing/limit-request`, { currentCount, currentLimit, message }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
